import { catchError, map, Observable,  throwError } from "rxjs";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";

import { Inject, Injectable, Optional } from "@angular/core";
import {
  BOKKZ_BASE_URL,
  CorporateTaxEntity,
  EnrichAangifte,
  IncomeTaxEntity, JaarrekeningEntity
} from "./api.bokkz.services";
import {
  Accountantsverklaring,
  EnrichAccountantsverklaringRequest, EnrichGrondslagByIdResult, EnrichGrondslagenRequest, EnrichGrondslagenResponse,
  Jaarrapportage,
  JaarrapportageEntity
} from './api.bokkz.jaarstukken';


@Injectable({
  providedIn: 'root'
})
export class SstUserServiceClient {
  private http: HttpClient;
  private readonly baseUrl: string;

  constructor(@Inject(HttpClient) http: HttpClient,
              @Optional() @Inject(BOKKZ_BASE_URL) baseUrl?: string) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";

  }

}

export interface BalanceProvider {
  id: string,
  name: string,
  supportsMultipleAdministrations: boolean,
  supportsBalanceByDateRange: boolean,
  supportsBalanceByPeriod: boolean,
}


export interface IdentityProvider {
  uri: string,
  method: string,
  query: string,
  headers: string,
  form: string
}

export interface DebugOptions {
  showElementStyles: boolean;
  showSbrId:  boolean;
  showJrElement: boolean;
  showNotInKvK: boolean;
  showMappingNotFound: boolean;
}

export interface RenderRequest {
  requestedReport: string;
  licensee: string;
  preparedBy: string;
  outputFormat: string;
  outputLanguage: string;
  stylesheet: string;
  copies: number;
  financialStatement: Jaarrapportage;
  accountantsverklaring: Accountantsverklaring;
  debugOptions: DebugOptions;
}

export interface RenderResponse {
  statusCode: number,
  statusMessage: string,
  fileName: string,
  base64File: string;
}

export interface EnvironmentInfo {
  id: string,
  name: string
}

export interface AdministrationInfo {
  id: string,
  name: string
}

@Injectable({
  providedIn: 'root',
})
export class PmClient {
  private http: HttpClient;
  private readonly baseUrl: string;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(BOKKZ_BASE_URL) baseUrl?: string,
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
  }

  getBalanceProviders(): Observable<BalanceProvider[]> {
    let url_ = this.baseUrl + '/api/v1/sst/balanceproviders';

    url_ = url_.replace(/[?&]$/, '');

    const options_ = {
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http.request('GET', url_, options_).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as BalanceProvider[]),
    );
  }

  getAdministrations(balanceProviderId: string): Observable<BalanceProvider[]> {
    let url_ = this.baseUrl + '/api/v1/sst/balanceproviders/{balanceProviderId}/administrations';
    if (balanceProviderId === undefined || balanceProviderId === null)
      throw new Error("The parameter 'balanceProviderId' must be defined.");
    url_ = url_.replace("{balanceProviderId}", encodeURIComponent("" + balanceProviderId));
    url_ = url_.replace(/[?&]$/, '');
    url_ = url_.replace(/[?&]$/, '');

    const options_ = {
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http.request('GET', url_, options_).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as BalanceProvider[]),
    );
  }

  getAdministration(balanceProviderId: string, administrationId: string): Observable<BalanceProvider[]> {
    let url_ = this.baseUrl + '/api/v1/sst/balanceproviders/{balanceProviderId}/administrations/{administrationId}';
    if (balanceProviderId === undefined || balanceProviderId === null)
      throw new Error("The parameter 'balanceProviderId' must be defined.");
    url_ = url_.replace("{balanceProviderId}", encodeURIComponent("" + balanceProviderId));
    if (administrationId === undefined || administrationId === null)
      throw new Error("The parameter 'administrationId' must be defined.");
    url_ = url_.replace("{administrationId}", encodeURIComponent("" + administrationId));
    url_ = url_.replace(/[?&]$/, '');

    const options_ = {
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http.request('GET', url_, options_).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as BalanceProvider[]),
    );
  }

  getAdministrationsEnvironment(balanceProviderId: string, environmentId: string): Observable<AdministrationInfo[]> {
    let url_ = this.baseUrl + `/api/v1/sst/balanceproviders/{balanceProviderId}/environments/{environmentId}/administrations`;
    if (environmentId === undefined || environmentId === null)
      throw new Error("The parameter 'environmentId' must be defined.");
    if (balanceProviderId === undefined || balanceProviderId === null)
      throw new Error("The parameter 'balanceProviderId' must be defined.");
    url_ = url_.replace("{balanceProviderId}", encodeURIComponent("" + balanceProviderId));
    url_ = url_.replace("{environmentId}", encodeURIComponent("" + environmentId));
    url_ = url_.replace(/[?&]$/, '');
    url_ = url_.replace(/[?&]$/, '');

    const options_ = {
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http.request('GET', url_, options_).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as AdministrationInfo[]),
    );
  }

  getAdministrationEnvironment(balanceProviderId: string, environmentId: string, administrationId: string): Observable<BalanceProvider[]> {
    let url_ = this.baseUrl + '/api/v1/sst/balanceproviders/{balanceProviderId}/environments/{environmentId}/administrations/{administrationId}';
    if (balanceProviderId === undefined || balanceProviderId === null)
      throw new Error("The parameter 'balanceProviderId' must be defined.");
    url_ = url_.replace("{environmentId}", encodeURIComponent("" + environmentId));
    if (environmentId === undefined || environmentId === null)
      throw new Error("The parameter 'environmentId' must be defined.");
    url_ = url_.replace("{balanceProviderId}", encodeURIComponent("" + balanceProviderId));
    if (administrationId === undefined || administrationId === null)
      throw new Error("The parameter 'administrationId' must be defined.");
    url_ = url_.replace("{administrationId}", encodeURIComponent("" + administrationId));
    url_ = url_.replace(/[?&]$/, '');

    const options_ = {
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http.request('GET', url_, options_).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as BalanceProvider[]),
    );
  }

  getBalanceProviderEnvironments(balanceProviderId: string): Observable<EnvironmentInfo[]> {
    let url_ = this.baseUrl + '/api/v1/sst/balanceproviders/{balanceProviderId}/environments';
    if (balanceProviderId === undefined || balanceProviderId === null)
      throw new Error("The parameter 'balanceProviderId' must be defined.");
    url_ = url_.replace("{balanceProviderId}", encodeURIComponent("" + balanceProviderId));
    url_ = url_.replace(/[?&]$/, '');
    url_ = url_.replace(/[?&]$/, '');

    const options_ = {
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };

    return this.http.request('GET', url_, options_).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as EnvironmentInfo[]),
    );
  }

  getBalanceProvider(providerId: string):Observable<BalanceProvider> {
    let url_ = this.baseUrl + "/api/v1/sst/balanceproviders/{providerId}";
    if (providerId === undefined || providerId === null)
      throw new Error("The parameter 'providerId' must be defined.");
    url_ = url_.replace("{providerId}", encodeURIComponent("" + providerId));
    url_ = url_.replace(/[?&]$/, '');
    const options_ = {
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };
    return this.http.request("GET",url_, options_).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as BalanceProvider),
    );
  }

  getIdentityProvider(providerId: string, code: string, state: string) {
    let url_ = this.baseUrl + "/api/v1/sst/identityprovider/{providerId}/authorize/callback/code="+code+"&state="+state;
    if (providerId === undefined || providerId === null)
      throw new Error("The parameter 'providerId' must be defined.");
    url_ = url_.replace("{providerId}", encodeURIComponent("" + providerId));
    url_ = url_.replace(/[?&]$/, '');
    const options_ = {
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };
    return this.http.request("GET",url_, options_).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as any),
    );
  }

  getIdentityProviderId(identityProviderId: string) {
    let url_ = this.baseUrl + "/api/v1/sst/identityproviders/{identityProviderId}";
    if (identityProviderId === undefined || identityProviderId === null)
      throw new Error("The parameter 'providerId' must be defined.");
    url_ = url_.replace("{identityProviderId}", encodeURIComponent("" + identityProviderId));
    url_ = url_.replace(/[?&]$/, '');
    const options_ = {
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    };
    return this.http.request("GET",url_, options_).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as any));
  }

  getIdentityProviders(providerId: string): Observable<IdentityProvider> {
    let url_ = this.baseUrl + "/api/v1/sst/identityproviders/{providerId}/authorize/";
    if (providerId === undefined || providerId === null)
    throw new Error("The parameter 'providerId' must be defined.");
    url_ = url_.replace("{providerId}", encodeURIComponent("" + providerId));
    url_ = url_.replace(/[?&]$/, '');
    const options_ = {
    observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  };

  return this.http.request("GET",url_, options_).pipe(
    catchError((error: any) => this.handleError(error)),
  map((response) => response.body as IdentityProvider));
  }

  postIdentityProviderEndSession(identityProviderId: string, environmentId: string): Observable<any> {
    if (!identityProviderId) throw new Error("The parameter 'identityProviderId' must be defined.");
    if (!environmentId) throw new Error("The parameter 'environmentId' must be defined.");

    // const url = `${this.baseUrl}/api/v1/sst/identityproviders/${encodeURIComponent(identityProviderId)}/environments/${encodeURIComponent(environmentId)}/endsession;
    const url = `${this.baseUrl}/api/v1/sst/identityproviders/${encodeURIComponent(identityProviderId)}/environments/${encodeURIComponent(environmentId)}/endsession`;

    const headers = new HttpHeaders({
      Accept: '*/*',
      'Content-Type': 'text/plain',
    });

    return this.http.post(url, '', { headers, observe: 'response', responseType: 'text'}).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as any));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ErrorEvent) {
      // Client-side or network error occurred
      console.error('An error occurred:', error.error.message);
    } else {
      // Backend returned an unsuccessful response code
      console.error(`Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return a thrown observable with a user-friendly error message
    return throwError(() => new Error('Something went wrong. Please try again later.'));
  }

  postBalanceProviderBalances(clientId: string,balanceProviderId: string, administrationId: string, partnerBoolean: boolean, tax: IncomeTaxEntity| CorporateTaxEntity|JaarrekeningEntity) {
    let url_ = this.baseUrl + '/api/v1/sst/balanceproviders/{clientId}/0/{balanceProviderId}/administrations/{administrationId}/periode-balances';
    if (clientId === undefined || clientId === null)
      throw new Error("The parameter 'clientId' must be defined.");
    url_ = url_.replace("{clientId}", encodeURIComponent("" + clientId));
    if (balanceProviderId === undefined || balanceProviderId === null)
      throw new Error("The parameter 'balanceProviderId' must be defined.");
    url_ = url_.replace("{balanceProviderId}", encodeURIComponent("" + balanceProviderId));
    if (administrationId === undefined || administrationId === null)
      throw new Error("The parameter 'administrationId' must be defined.");
    url_ = url_.replace("{administrationId}", encodeURIComponent("" + administrationId));

    url_ = url_.replace(/[?&]$/, '');

    const bsnIb = partnerBoolean ? tax.aangifte?.partnerAangifte.aangever?.bsn : tax.aangifte?.aangever?.bsn
    const enrichtAangifte = {} as EnrichAangifte;
    enrichtAangifte.aangifte=JSON.stringify(tax)
    enrichtAangifte.bsn=bsnIb;
    return this.http.request('POST', url_, {
      body: enrichtAangifte,
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    }).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as IncomeTaxEntity| CorporateTaxEntity| JaarrapportageEntity),
    );
  }

  postBalanceProviderBalancesEnvironment(clientId: string,balanceProviderId: string, environmentId:string, administrationId: string, partnerBoolean: boolean, tax: IncomeTaxEntity| CorporateTaxEntity|JaarrekeningEntity) {
    let url_ = this.baseUrl + '/api/v1/sst/balanceproviders/{clientId}/0/{balanceProviderId}/environments/{environmentId}/administrations/{administrationId}/periode-balances';
    if (clientId === undefined || clientId === null)
      throw new Error("The parameter 'clientId' must be defined.");
    if (environmentId === undefined || environmentId === null)
      throw new Error("The parameter 'environmentId' must be defined.");
    if (balanceProviderId === undefined || balanceProviderId === null)
      throw new Error("The parameter 'balanceProviderId' must be defined.");
    if (administrationId === undefined || administrationId === null)
      throw new Error("The parameter 'administrationId' must be defined.");
    url_ = url_.replace("{clientId}", encodeURIComponent("" + clientId));
    url_ = url_.replace("{balanceProviderId}", encodeURIComponent("" + balanceProviderId));
    url_ = url_.replace("{environmentId}", encodeURIComponent("" + environmentId));
    url_ = url_.replace("{administrationId}", encodeURIComponent("" + administrationId));
    url_ = url_.replace(/[?&]$/, '');

    const bsnIb = partnerBoolean ? tax.aangifte?.partnerAangifte.aangever?.bsn : tax.aangifte?.aangever?.bsn
    const enrichtAangifte = {} as EnrichAangifte;
    enrichtAangifte.aangifte=JSON.stringify(tax)
    enrichtAangifte.bsn=bsnIb;
    return this.http.request('POST', url_, {
      body: enrichtAangifte,
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    }).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as IncomeTaxEntity| CorporateTaxEntity| JaarrapportageEntity),
    );
  }

  postPmRenderRequestJaarrapportage(renderRequest: RenderRequest): Observable<RenderResponse> {
    let url_ = this.baseUrl + '/api/v1/sst/render/renderreport';

    url_ = url_.replace(/[?&]$/, '');

    return this.http.request('POST', url_, {
      body: renderRequest,
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    }).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as RenderResponse),
    );
  }

  postPmRenderRequestPublicatiestukken(renderRequest: RenderRequest): Observable<RenderResponse> {
    let url_ = this.baseUrl + '/api/v1/sst/render/renderpubs';

    url_ = url_.replace(/[?&]$/, '');

    return this.http.request('POST', url_, {
      body: renderRequest,
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    }).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as RenderResponse),
    );
  }

}

@Injectable({
  providedIn: 'root',
})
export class EnrichClient {
  private http: HttpClient;
  private readonly baseUrl: string;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(BOKKZ_BASE_URL) baseUrl?: string,
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
  }

  getEnrichedGrondslagen(request: EnrichGrondslagenRequest): Observable<EnrichGrondslagenResponse> {
    let url_ = this.baseUrl + '/api/v1/jr/enrich-grondslagen';

    url_ = url_.replace(/[?&]$/, '');

    return this.http.request('POST', url_, {
      body: request,
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    }).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as EnrichGrondslagenResponse),
    );
  }

  getEnrichedGrondslagenByIds(request: EnrichGrondslagenRequest): Observable<EnrichGrondslagByIdResult[]> {
    let url_ = this.baseUrl + '/api/v1/jr/enrich-grondslagen-by-ids';

    url_ = url_.replace(/[?&]$/, '');

    return this.http.request('POST', url_, {
      body: request,
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    }).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as EnrichGrondslagByIdResult[]),
    );
  }

  getEnrichedAccountantsverklaring(request: EnrichAccountantsverklaringRequest): Observable<Accountantsverklaring> {
    let url_ = this.baseUrl + '/api/v1/jr/enrich-accountantsverklaring';

    url_ = url_.replace(/[?&]$/, '');

    return this.http.request('POST', url_, {
      body: request,
      observe: 'response' as const,
      responseType: 'json' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
    }).pipe(
      catchError((error: any) => this.handleError(error)),
      map((response) => response.body as Accountantsverklaring),
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ErrorEvent) {
      // Client-side or network error occurred
      console.error('An error occurred:', error.error.message);
    } else {
      // Backend returned an unsuccessful response code
      console.error(`Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return a thrown observable with a user-friendly error message
    return throwError(() => new Error('Something went wrong. Please try again later.'));
  }

}
